/**
 * The dictionary admin page's "Third-party supplier pricing" panel —
 * third-party fulfilment, phase 2 (docs/features/catalogue-third-party-
 * fulfilment/07_phase_2_product_brief.md, journeys F and G).
 *
 * Two operational controls, both runtime and both recorded with who, when and
 * why:
 * - **Show / hide supplier pricing (#90)** — off by default. Hidden, every
 *   catalogue and estimate screen is exactly today's; anything already sent or
 *   ordered on a supplier route carries on.
 * - **Suspend a supplier (#87)** — takes one supplier out of every quote at
 *   once, e.g. during an outage or a quality problem.
 *
 * Presentational: the page loads the controls and performs the changes. The
 * switch never flips on its own: it opens the reason dialog, and the panel
 * re-renders from what the backend returns.
 */
import { useState } from "react";
import type {
  SupplierPricingControls,
  SupplierRouteControl,
} from "@shared-core/data/api-types";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Label } from "../../primitives/label";
import { Switch } from "../../primitives/switch";
import { ReasonConfirmDialog } from "./ReasonConfirmDialog";

export interface SupplierPricingControlsPanelProps {
  controls: SupplierPricingControls;
  busy?: boolean;
  error?: string | null;
  onSetShown: (shown: boolean, reason: string) => void;
  onSetSuspended: (
    routeCode: string,
    suspended: boolean,
    reason: string | null,
  ) => void;
}

export function SupplierPricingControlsPanel({
  controls,
  busy = false,
  error = null,
  onSetShown,
  onSetSuspended,
}: SupplierPricingControlsPanelProps) {
  const latest = controls.history[0];
  const [confirming, setConfirming] = useState(false);
  return (
    <section
      aria-label="Third-party supplier pricing"
      className="space-y-3 rounded-lg border border-border px-4 py-3 text-sm"
    >
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Third-party supplier pricing
      </h2>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="flex flex-wrap items-center gap-2">
            <Badge variant={controls.shown ? "success" : "neutral"}>
              {controls.shown ? "Shown" : "Hidden"}
            </Badge>
            {controls.shown
              ? "CSRs see a price option for each supplier that can make the item, beside the in-house options, and can quote and order on it."
              : "Catalogue screens show in-house prices only, with the Speed choice, exactly as before."}
          </p>
          {latest && (
            <p className="text-xs text-muted-foreground">
              {latest.shown ? "Shown" : "Hidden"} by {latest.changedBy} on{" "}
              {formatDateTimeOrDash(latest.changedAt)}: “{latest.reason}”
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Whatever this says, quotes and orders already on a supplier route
            carry on — currently {controls.inFlight.quotesAwaitingCustomer}{" "}
            quotes awaiting the customer and{" "}
            {controls.inFlight.ordersAwaitingPlacement} awaiting placement with
            the supplier. The cost matrix and the overnight updates are not
            affected.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* The switch only asks: it flips once the reason is confirmed. */}
          <Switch
            id="supplier-pricing-shown"
            checked={controls.shown}
            disabled={busy}
            onCheckedChange={() => setConfirming(true)}
          />
          <Label htmlFor="supplier-pricing-shown" className="font-normal">
            Show supplier pricing
          </Label>
          <ReasonConfirmDialog
            open={confirming}
            onOpenChange={setConfirming}
            title={
              controls.shown
                ? "Hide supplier pricing?"
                : "Show supplier pricing?"
            }
            description={
              controls.shown
                ? "CSRs stop seeing supplier options on new quotes and orders, and every catalogue screen goes back to in-house prices with the Speed choice. Quotes and orders already on a supplier route carry on."
                : "Every CSR will see supplier options on catalogue items that have them, and can quote and order on them."
            }
            reasonLabel={controls.shown ? "Why hide it?" : "Why show it?"}
            confirmLabel={controls.shown ? "Hide" : "Show"}
            destructive={controls.shown}
            onConfirm={(reason) => onSetShown(!controls.shown, reason)}
          />
        </div>
      </div>

      <div className="space-y-2 border-t border-border pt-3">
        <h3 className="font-medium">Suppliers</h3>
        <ul className="space-y-2">
          {controls.suppliers.map((s) => (
            <SupplierRow
              key={s.routeCode}
              supplier={s}
              busy={busy}
              onSetSuspended={onSetSuspended}
            />
          ))}
        </ul>
      </div>
    </section>
  );
}

function SupplierRow({
  supplier,
  busy,
  onSetSuspended,
}: {
  supplier: SupplierRouteControl;
  busy: boolean;
  onSetSuspended: SupplierPricingControlsPanelProps["onSetSuspended"];
}) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-3">
      <div className="space-y-0.5">
        <p className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{supplier.routeLabel}</span>
          {supplier.suspended ? (
            <Badge variant="warning">Suspended</Badge>
          ) : (
            <span className="text-muted-foreground">
              Offered on {supplier.availableItemCount} dictionary item
              {supplier.availableItemCount === 1 ? "" : "s"}
            </span>
          )}
        </p>
        {supplier.suspended && (
          <p className="text-xs text-muted-foreground">
            Suspended by {supplier.suspendedBy} on{" "}
            {formatDateTimeOrDash(supplier.suspendedAt)}: “
            {supplier.suspendedReason}”. No quote offers it until it is resumed.
          </p>
        )}
      </div>
      {supplier.suspended ? (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onSetSuspended(supplier.routeCode, false, null)}
        >
          Resume {supplier.routeLabel}
        </Button>
      ) : (
        <ReasonConfirmDialog
          disabled={busy}
          trigger={
            <Button size="sm" variant="outline" disabled={busy}>
              Suspend {supplier.routeLabel}
            </Button>
          }
          title={`Suspend ${supplier.routeLabel}?`}
          description={`${supplier.routeLabel} drops out of every quote at once, in every catalogue, until it is resumed. Estimates already sent on it carry on.`}
          reasonLabel={`Why suspend ${supplier.routeLabel}?`}
          confirmLabel="Suspend"
          destructive
          onConfirm={(reason) =>
            onSetSuspended(supplier.routeCode, true, reason)
          }
        />
      )}
    </li>
  );
}
